// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, { useState, useEffect, useRef } from 'react';
import { LiliButton } from '@lili/lili-frontend-common';

import { useTranslation } from 'react-i18next';
import { Input } from '../Input';
import { IInputContent, IWelcomeHandleInput } from '../../types';
import { WELCOME_TO_LILI_SCREEN_DATA } from '../../flows/login/constants';
import { KEY_CODES } from '../../constants/general';
import { BACK_BUTTON_STYLE } from '../MFA/helpers/constants';

export interface IInputList {
  TITLE: string,
  VALUE?: any,
  TYPE: string,
  VALIDATION: RegExp | string,
  ERROR: string,
  AUTOCOMPLETE?: string,
  MAX_LENGTH?: number,
  REPLACE?: RegExp | string
  DISABLED?: boolean,
  INITIAL_FORMAT?: boolean,
  OPTIONAL?: boolean;
  MAX_PRICE?: number;
}

export interface IFormProps {
    inputsList: IInputList[];
    onSubmit: (data: any) => void;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    error: string;
    submitButtonText?: string;
    submitButtonClassName?: string;
    onInputsFocus: () => void;
    isSubmitValidation?: boolean;
    formClassName?: string;
    additionalActions?: React.ReactNode;
    onFormDataChange?: (data: any) => void;
}

export const Form = ({
  inputsList,
  onSubmit,
  isLoading,
  isError,
  error,
  onInputsFocus,
  isSuccess,
  submitButtonText,
  isSubmitValidation,
  formClassName,
  submitButtonClassName,
  additionalActions,
  onFormDataChange,
}:
  IFormProps) => {
  const buildInitialState = () => {
    const initialStateObj: any = {};
    inputsList.forEach(({ TYPE }) => {
      initialStateObj[TYPE] = '';
    });
    return initialStateObj;
  };
  const [shouldRunValidation, setShouldRunValidation] = useState<boolean>(false);
  const [formValidationStatus, setFormValidationStatus] = useState<boolean>(false);
  const [formData, _setFormData] = useState<{}>(buildInitialState());
  const formRef = useRef(formData);

  const setFormData = (data: {}) => {
    _setFormData((prevState) => {
      const newState = { ...prevState, ...data };
      formRef.current = newState;
      return newState;
    });
  };

  const { LOGIN_BTN } = WELCOME_TO_LILI_SCREEN_DATA;

  const isFormValid = () => {
    if (isError && error) {
      return false;
    }
    const structuredFormData = Object.values<IInputContent>(formData);
    if (structuredFormData.some(({ internalError }) => internalError)) {
      return false;
    }
    return structuredFormData.every(({ validation, value, optional }) => ((optional && !value) ? true : new RegExp(validation).test(value)));
  };

  const triggerInternalInputValidation = () => {
    setShouldRunValidation(true);
    setTimeout(() => {
      setShouldRunValidation(false);
    }, 200);
  };

  const submitForm = () => {
    if (!isFormValid()) {
      triggerInternalInputValidation();
      return;
    }
    onSubmit(formRef.current);
  };

  const onEnterClick = (e: KeyboardEvent) => {
    if (e.code === KEY_CODES.ENTER) {
      e.preventDefault();
      const submitBtn = document.getElementById('submitForm');
      submitBtn?.click();
    }
  };

  const onChange = (inputData: IWelcomeHandleInput) => {
    const {
      key, value, validation, optional, internalError,
    } = inputData;
    setFormData({
      [key]: {
        validation, value, optional, internalError,
      },
    });
  };

  useEffect(() => {
    const formStatus = isFormValid();
    setFormValidationStatus(formStatus);
    if (onFormDataChange) onFormDataChange(formData);
  }, [formData]);

  useEffect(() => {
    if (!inputsList.length) return;
    inputsList.forEach((inputData) => {
      const {
        TYPE, VALUE, VALIDATION, OPTIONAL,
      } = inputData;
      setFormData({ [TYPE]: { validation: VALIDATION, value: VALUE, optional: OPTIONAL } });
    });
  }, [inputsList]);

  useEffect(() => {
    window.addEventListener('keydown', onEnterClick);

    return () => {
      window.removeEventListener('keydown', onEnterClick);
    };
  }, []);

  const showMainErrorMessage = (index: number) => ((index + 1 === inputsList.length) && isError);

  const { t } = useTranslation();

  return (
    <form aria-label='form' id='form' className={formClassName}>
      {inputsList.map(({
        TITLE: title,
        TYPE: type,
        VALIDATION: validation,
        ERROR: inputError,
        AUTOCOMPLETE: autocomplete,
        MAX_LENGTH: maxLen,
        REPLACE: replace,
        VALUE: defaultValue,
        DISABLED: disabled,
        INITIAL_FORMAT: initialFormat,
        OPTIONAL: optional,
        MAX_PRICE: maxPrice,
      }, index) => (
        <Input
          id={`genericFormInput${title.replaceAll(' ', '')}`}
          shouldValidate={shouldRunValidation}
          key={title}
          onChange={onChange}
          title={t(title)}
          type={type}
          error={showMainErrorMessage(index) ? error : t(inputError)}
          validation={validation}
          autocomplete={autocomplete}
          isError={showMainErrorMessage(index)}
          resetError={isLoading}
          onFocus={onInputsFocus}
          maxLen={maxLen}
          replace={replace}
          defaultValue={defaultValue}
          disabled={disabled}
          shouldFormatOnMount={initialFormat}
          isOptional={optional}
          maxPrice={maxPrice}
        />
      ))}
      <div>
        {additionalActions && additionalActions}
      </div>
      <LiliButton
        disabled={isSubmitValidation ? !formValidationStatus : false}
        id='submitForm'
        className={`${!isSubmitValidation ? (BACK_BUTTON_STYLE.CTA_BUTTON) : isError ? LOGIN_BTN.INVALID_CLASS_NAME : formValidationStatus ? (BACK_BUTTON_STYLE.CTA_BUTTON) : LOGIN_BTN.INVALID_CLASS_NAME} ${submitButtonClassName} ${submitButtonClassName}`}
        title={submitButtonText ?? t(LOGIN_BTN.TITLE)}
        onClick={submitForm}
        isLoading={isLoading || isSuccess}
      />
    </form>
  );
};
